import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FoodSearch, RecentAndFavorites, RecipePicker } from "@/components/journal/FoodPickers";
import { BarcodeScanner } from "@/components/journal/BarcodeScanner";
import { ProductForm } from "@/components/journal/ProductForm";
import { CustomFoodForm } from "@/components/journal/CustomFoodForm";
import { lookupBarcode, newEntryId, errorKey } from "@/lib/foodlog";

// Feuille « Ajouter un aliment » : recherche / scan / récents / recettes / création → fiche → ajout au journal.
export function AddFoodSheet({ open, onClose, date, memberId, meal: initialMeal, onAdded }) {
  const { t } = useLang();
  const qc = useQueryClient();
  const [meal, setMeal] = useState(initialMeal || "lunch");
  const [picked, setPicked] = useState(null); // {food} | {recipe} | {recent}
  const [createInit, setCreateInit] = useState(null);
  const [notFound, setNotFound] = useState(null);
  const [tab, setTab] = useState("search");
  const [busy, setBusy] = useState(false);
  const [favIds, setFavIds] = useState(null);

  const reset = () => { setPicked(null); setCreateInit(null); setNotFound(null); };
  const close = () => { reset(); onClose(); };

  const post = async (body) => {
    setBusy(true);
    try {
      await api.post("/food/entries", { entry_id: newEntryId(), date, member_id: memberId, meal, ...body });
      toast.success(t("j_added")); onAdded(); close();
    } catch (e) { toast.error(t(errorKey(e))); } finally { setBusy(false); }
  };

  const onCode = async (code) => {
    setBusy(true); setNotFound(null);
    try { const f = await lookupBarcode(code); if (f) setPicked({ food: f }); else setNotFound(code); }
    catch { toast.error(t("j_off_unavailable")); } finally { setBusy(false); }
  };

  const loadFavs = async () => { if (favIds) return favIds; const ids = (await api.get("/food/favorites")).data.favorites.map((f) => f.food_id); setFavIds(ids); return ids; };
  const toggleFav = async (food) => {
    const ids = await loadFavs(); const has = ids.includes(food.food_id);
    await api[has ? "delete" : "put"](`/food/favorites/${food.food_id}`);
    setFavIds(has ? ids.filter((i) => i !== food.food_id) : [...ids, food.food_id]); qc.invalidateQueries({ queryKey: ["food-favorites"] });
  };

  const createCustom = async (payload) => {
    setBusy(true);
    try { const { data } = await api.post("/food/custom", payload); toast.success(t("j_created")); qc.invalidateQueries({ queryKey: ["food-custom"] }); setCreateInit(null); setPicked({ food: data.food }); }
    catch (e) { toast.error(t(errorKey(e))); } finally { setBusy(false); }
  };

  const title = picked ? (picked.food?.name || picked.recipe?.name || picked.recent?.label) : createInit ? t("j_tab_create") : t("j_add_food");
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="sm:max-w-lg rounded-3xl" data-testid="journal-add-sheet">
        <DialogHeader><DialogTitle className="flex items-center gap-2 pr-6">
          {(picked || createInit) && <button onClick={reset} className="grid place-items-center h-8 w-8 rounded-full hover:bg-secondary" aria-label="back" data-testid="journal-sheet-back"><ArrowLeft className="h-4 w-4" /></button>}
          <span className="truncate">{title}</span></DialogTitle></DialogHeader>

        {picked?.food && <ProductForm food={picked.food} meal={meal} onMeal={setMeal} busy={busy} favorite={favIds?.includes(picked.food.food_id)} onToggleFavorite={() => toggleFav(picked.food)}
          onAdd={({ quantity, unit }) => post({ source: picked.food.source === "custom" ? "custom" : "product", ref_id: picked.food.food_id, quantity, unit })} />}
        {picked?.recipe && <ProductForm food={{ ...picked.recipe, recipe: true }} meal={meal} onMeal={setMeal} busy={busy}
          onAdd={({ quantity }) => post({ source: "recipe", ref_id: picked.recipe.recipe_id, quantity, unit: "portion" })} />}
        {picked?.recent && <ProductForm food={{ name: picked.recent.label, brand: picked.recent.brand, recipe: picked.recent.source === "recipe" || !picked.recent.basis?.per_100g, nutrition: picked.recent.basis?.per_portion || picked.recent.nutrition, per_100g: picked.recent.basis?.per_100g, serving_grams: picked.recent.basis?.serving_grams, complete: true }} meal={meal} onMeal={setMeal} busy={busy}
          onAdd={({ quantity, unit }) => picked.recent.ref_id && picked.recent.source !== "manual"
            ? post({ source: picked.recent.source, ref_id: picked.recent.ref_id, quantity, unit: picked.recent.source === "recipe" ? "portion" : unit })
            : post({ source: "manual", label: picked.recent.label, brand: picked.recent.brand, quantity, unit: picked.recent.unit, nutrition: Object.fromEntries(Object.entries(picked.recent.nutrition).map(([k, v]) => [k, v == null ? null : v * quantity / picked.recent.quantity])) })} />}
        {createInit && <CustomFoodForm initial={createInit} onCreate={createCustom} busy={busy} />}

        {!picked && !createInit && (
          <Tabs value={tab} onValueChange={setTab} className="min-w-0">
            <TabsList className="flex w-full h-auto rounded-2xl overflow-x-auto justify-start" data-testid="journal-tabs">
              {["search", "scan", "recent", "recipes", "create"].map((k) => <TabsTrigger key={k} value={k} className="text-[11px] sm:text-xs px-2.5 py-1.5 rounded-xl whitespace-nowrap shrink-0 sm:shrink sm:flex-1" data-testid={`journal-tab-${k}`}>{t(`j_tab_${k}`)}</TabsTrigger>)}
            </TabsList>
            <TabsContent value="search"><FoodSearch onFood={(f) => setPicked({ food: f })} onRecipe={(r) => setPicked({ recipe: r })} onCreate={(init) => setCreateInit(init)} /></TabsContent>
            <TabsContent value="scan">
              <BarcodeScanner onCode={onCode} />
              {notFound && <div className="mt-3 text-sm text-muted-foreground" data-testid="journal-not-found">{t("j_not_found")} <button className="text-primary underline underline-offset-4" onClick={() => setCreateInit({ barcode: notFound })} data-testid="journal-not-found-create">{t("j_create_this")}</button></div>}
            </TabsContent>
            <TabsContent value="recent"><RecentAndFavorites memberId={memberId} onFood={(f) => setPicked({ food: f })} onRecent={(r) => setPicked({ recent: r })} /></TabsContent>
            <TabsContent value="recipes"><RecipePicker onRecipe={(r) => setPicked({ recipe: r })} /></TabsContent>
            <TabsContent value="create"><CustomFoodForm onCreate={createCustom} busy={busy} /></TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
